/**
 * V4 Migration Module: V3 ↔ V4 conversion, V4 validation, and the single
 * entry point readers use to bring any song to the current format.
 *
 * V4 differs from V3 in one thing: a row holds `bars`, each bar holds
 * `slots`, instead of a flat `slots` list. Every V3 slot becomes one bar
 * holding that slot, keeping the slot id; nothing else changes. The reverse
 * is lossless while every bar holds one slot and no slot sets `beats` —
 * true for every song until the beats feature ships — which is what lets
 * the apps hold V4 in memory and keep writing V3 for older app versions
 * (see `resolveWriteVersion`).
 *
 * Rules shared by every version:
 * - Readers upgrade forever: V2 and V3 data is accepted for good.
 * - `version` only changes for breaking shape changes. Additive fields are
 *   added without a bump, and every reader keeps unknown fields, so a file
 *   from a slightly newer app still round-trips through an older one.
 * - A version above `SCHEMA_VERSION_LATEST` is "made with a newer app":
 *   `UnsupportedSchemaVersionError`, which every reader turns into an
 *   "update the app" message instead of a crash.
 */
import { normalizeSlotBeats } from './barBeats.js';
import { createBar } from './barUtils.js';
import { detectVersion, migrateStateV2ToV3, validateAndNormalizeSongShell, validateAndNormalizeStateV3, validateSlotV3, } from './v3Migration.js';
/** The newest format this package reads and writes. */
export const SCHEMA_VERSION_LATEST = 4;
/**
 * The oldest format a writer may still be asked for. Readers upgrade from
 * anything older (V1, V2) forever, but nothing writes those shapes any more.
 */
export const SCHEMA_VERSION_OLDEST_WRITABLE = 3;
/**
 * True for an integer between `SCHEMA_VERSION_OLDEST_WRITABLE` and
 * `SCHEMA_VERSION_LATEST`. Write policies parse their configured version
 * through this, so a value this package does not know yet (the next bump,
 * read by an app built before it) falls back to the policy's default instead
 * of being written as something no reader understands.
 */
export function isWritableSchemaVersion(value) {
    return (typeof value === 'number' &&
        Number.isInteger(value) &&
        value >= SCHEMA_VERSION_OLDEST_WRITABLE &&
        value <= SCHEMA_VERSION_LATEST);
}
// ============================================================================
// Errors
// ============================================================================
/** The data was written by a newer app than this one. */
export class UnsupportedSchemaVersionError extends Error {
    constructor(version, latest = SCHEMA_VERSION_LATEST) {
        super(`Unsupported song format version ${version}: this app reads up to version ${latest}`);
        this.version = version;
        this.latest = latest;
        this.name = 'UnsupportedSchemaVersionError';
    }
}
/** The song holds something V3 cannot express (a bar with several slots, or beats). */
export class LossySchemaDowngradeError extends Error {
    constructor(message = 'Cannot convert to version 3 without losing bar subdivisions') {
        super(message);
        this.name = 'LossySchemaDowngradeError';
    }
}
/** V1 data must go through the app's V1 importer before `upgradeToLatest`. */
export class LegacySchemaVersionError extends Error {
    constructor() {
        super('Version 1 data must be migrated to version 2 by the app before upgrading');
        this.name = 'LegacySchemaVersionError';
    }
}
/** `instanceof` can fail across bundles, so readers test the name too. */
export const isUnsupportedSchemaVersionError = (error) => error instanceof UnsupportedSchemaVersionError ||
    (typeof error === 'object' && error !== null && error.name === 'UnsupportedSchemaVersionError');
// ============================================================================
// Structure
// ============================================================================
/** True when the song holds bars (a V4 object), judged by its structure, not its `version`. */
export function isStateV4(state) {
    for (const section of state.sections) {
        for (const row of section.rows) {
            return 'bars' in row;
        }
    }
    return state.version === 4;
}
/** True when every bar holds one slot and no slot sets beats: what V3 can express. */
export function areSectionsLosslessToV3(sections) {
    return sections.every(section => section.rows.every(row => row.bars.every(bar => bar.slots.length === 1 && bar.slots[0].beats == null)));
}
/** True when `downgradeStateV4ToV3` would keep everything: one slot per bar, no beats. */
export function isLosslessToV3(state) {
    return !isStateV4(state) || areSectionsLosslessToV3(state.sections);
}
/**
 * The version a writer should emit: the requested one, unless that would
 * lose data, in which case the current version. Data is never dropped
 * silently to satisfy a policy.
 *
 * Next bump: this becomes "the requested version, or the lowest version at or
 * above it that holds everything" (see docs/schema-bump-playbook.md).
 */
export function resolveWriteVersion(state, requested) {
    return requested === 3 && !isLosslessToV3(state) ? 4 : requested;
}
// ============================================================================
// Conversion
// ============================================================================
/** `obj` with key `from` replaced by `to` at the same position (key order is what files are compared by). */
function withKeyRenamed(obj, from, to, value) {
    const out = {};
    let placed = false;
    for (const [key, current] of Object.entries(obj)) {
        if (key === from) {
            out[to] = value;
            placed = true;
        }
        else {
            out[key] = current;
        }
    }
    if (!placed) {
        out[to] = value;
    }
    return out;
}
/** V3 sections as V4: each slot becomes one bar holding it, same slot id, every other field kept. */
export function migrateSectionsV3ToV4(sections, generateId) {
    return sections.map(section => ({
        ...section,
        rows: section.rows.map(row => withKeyRenamed(row, 'slots', 'bars', row.slots.map(slot => createBar({ ...slot }, generateId)))),
    }));
}
/**
 * Migrate a validated V3 state to V4: each slot becomes one bar holding it,
 * with the same slot id. Every other field, known or not, is copied as is.
 * Bar ids are derived from the slot ids (`barIdForSlot`) unless a generator
 * is given.
 */
export function migrateStateV3ToV4(v3, generateId) {
    return { ...v3, version: 4, sections: migrateSectionsV3ToV4(v3.sections, generateId) };
}
const withoutBeats = (slot) => {
    if (!('beats' in slot)) {
        return { ...slot };
    }
    const { beats: _beats, ...rest } = slot;
    return rest;
};
/** V4 sections as V3 (one slot per bar). Throws `LossySchemaDowngradeError` when a bar holds more. */
export function downgradeSectionsV4ToV3(sections) {
    if (!areSectionsLosslessToV3(sections)) {
        throw new LossySchemaDowngradeError();
    }
    return sections.map(section => ({
        ...section,
        rows: section.rows.map(row => withKeyRenamed(row, 'bars', 'slots', row.bars.map(bar => withoutBeats(bar.slots[0])))),
    }));
}
/**
 * Convert a V4 state back to V3 for older readers. Lossless only while every
 * bar holds one slot and no slot sets `beats` (`isLosslessToV3`); otherwise
 * throws `LossySchemaDowngradeError` rather than dropping subdivisions.
 */
export function downgradeStateV4ToV3(v4) {
    return { ...v4, version: 3, sections: downgradeSectionsV4ToV3(v4.sections) };
}
export function convertStateToVersion(state, target, generateId) {
    if (isStateV4(state)) {
        return target === 4 ? withVersionLabel(state, 4) : downgradeStateV4ToV3(state);
    }
    return target === 3 ? withVersionLabel(state, 3) : migrateStateV3ToV4(state, generateId);
}
/** The same object when its `version` already matches its shape; a relabelled copy otherwise. */
function withVersionLabel(state, version) {
    return state.version === version ? state : { ...state, version };
}
// ============================================================================
// V4 Validation
// ============================================================================
/**
 * Validate that a state object conforms to ChordCakeStateV4, normalizing it
 * in place with the same leniency as `validateAndNormalizeStateV3`: slot
 * fields and row time signatures are backfilled to null, section repeat/note
 * and the song meta are cleaned rather than rejected, a present `beats` is
 * cleaned to a positive multiple of half a beat or null (`normalizeSlotBeats`
 * in `barBeats.ts`; the bar rules themselves are applied on read by
 * `layoutBarSlots`, never enforced here) and an absent one stays absent.
 * Every bar must hold at least one slot. Unknown fields are kept.
 *
 * Because it mutates its argument, do not pass frozen objects.
 */
export function validateAndNormalizeStateV4(state) {
    if (state.version !== 4) {
        throw new Error('Expected version: 4');
    }
    validateAndNormalizeSongShell(state, (row, path) => {
        if (!Array.isArray(row.bars)) {
            throw new Error(`Invalid ${path}.bars: expected array`);
        }
        for (let bi = 0; bi < row.bars.length; bi++) {
            const bar = row.bars[bi];
            const barPath = `${path}.bar[${bi}]`;
            if (typeof bar !== 'object' || bar === null) {
                throw new Error(`Invalid ${barPath}: expected object`);
            }
            if (typeof bar.id !== 'string') {
                throw new Error(`Invalid ${barPath}.id: expected string`);
            }
            if (!Array.isArray(bar.slots) || bar.slots.length === 0) {
                throw new Error(`Invalid ${barPath}.slots: expected a non-empty array`);
            }
            for (let sli = 0; sli < bar.slots.length; sli++) {
                const slot = bar.slots[sli];
                validateSlotV3(slot, `${barPath}.slot[${sli}]`);
                if (slot.beats !== undefined) {
                    slot.beats = normalizeSlotBeats(slot.beats);
                }
            }
        }
    });
}
// ============================================================================
// Reader entry point
// ============================================================================
/**
 * Any song this package reads (V2, V3 or V4), validated and brought to V4.
 * V3 and V4 input is normalized in place; the result shares the input's
 * slot and chord objects.
 *
 * Throws `UnsupportedSchemaVersionError` for a version above
 * `SCHEMA_VERSION_LATEST`, `LegacySchemaVersionError` for V1 (the apps own
 * the V1 importer; run it first), and a descriptive `Error` for invalid data.
 */
export function upgradeToLatest(data, generateId) {
    if (typeof data !== 'object' || data === null || Array.isArray(data)) {
        throw new Error('Invalid song: expected an object');
    }
    const version = detectVersion(data);
    if (version > SCHEMA_VERSION_LATEST) {
        throw new UnsupportedSchemaVersionError(version);
    }
    switch (version) {
        case 4:
            validateAndNormalizeStateV4(data);
            return data;
        case 3:
            validateAndNormalizeStateV3(data);
            return migrateStateV3ToV4(data, generateId);
        case 2: {
            const v3 = migrateStateV2ToV3(data);
            validateAndNormalizeStateV3(v3);
            return migrateStateV3ToV4(v3, generateId);
        }
        default:
            throw new LegacySchemaVersionError();
    }
}
